import { useFormContext } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { DownloadIcon } from '@/icons';
import { buildBakedAvatarUrl, useAvatarCatalog } from '@/shared/features/user-avatar';
import { Button } from '@/ui_v3';
import { getPartHex } from '../avatarDecorate.logic';
import { type AvatarDecorateForm } from '../schema';
const DOWNLOAD_IMAGE_SIZE = 256;
const FETCH_RETRY = 3;

/**
 * 방금 바꾼 아바타 조합은 baked PNG 가 아직 CDN 에 안 구워져 있을 수 있다.
 * 첫 요청이 굽기를 트리거하지만 그 사이 404(+CORS 헤더 없는 응답 → "Failed to
 * fetch")가 날 수 있어, 짧은 backoff 로 재시도해 굽는 시간을 흡수한다.
 * (코어 useDownloadAvatar 의 single-shot fetch race 를 보완.)
 */
async function fetchBakedAvatar(url: string): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= FETCH_RETRY; attempt++) {
    try {
      const res = await fetch(url, {
        cache: 'no-store'
      });
      if (res.ok) return res;
      lastError = new Error(`Failed to load avatar: ${res.status}`);
    } catch (e) {
      lastError = e;
    }
    if (attempt < FETCH_RETRY) {
      await new Promise(resolve => setTimeout(resolve, 400 * (attempt + 1)));
    }
  }
  throw lastError;
}

/**
 * 코어 useDownloadAvatar(libs/feature/play) 포팅 + baking warm-up 재시도.
 * baked PNG 를 fetch→blob→objectURL→img→canvas(256, 의상색 배경) 로 그려 다운로드.
 * blob objectURL 은 same-origin 이라 canvas 오염 없이 toBlob 가능.
 */
export const DownloadAvatarButton = () => {
  const {
    getValues
  } = useFormContext<AvatarDecorateForm>();
  const {
    t
  } = useTranslation('common');
  const {
    data: catalog
  } = useAvatarCatalog('quiz');
  const handleDownload = async () => {
    if (typeof window === 'undefined') return;
    const avatar = getValues('avatar');
    const imageUrl = buildBakedAvatarUrl(avatar);
    try {
      const response = await fetchBakedAvatar(imageUrl);
      const blob = await response.blob();
      const imageObjectUrl = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = DOWNLOAD_IMAGE_SIZE;
        canvas.height = DOWNLOAD_IMAGE_SIZE;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.imageSmoothingEnabled = false;
          const bgColor = catalog ? getPartHex(catalog, 'clothes', Math.abs(avatar.clothesId)) ?? '#CCCCCC' : '#CCCCCC';
          ctx.fillStyle = bgColor;
          ctx.fillRect(0, 0, DOWNLOAD_IMAGE_SIZE, DOWNLOAD_IMAGE_SIZE);
          ctx.drawImage(img, 0, 0, DOWNLOAD_IMAGE_SIZE, DOWNLOAD_IMAGE_SIZE);
          canvas.toBlob(resizedBlob => {
            if (resizedBlob) {
              const url = URL.createObjectURL(resizedBlob);
              const link = document.createElement('a');
              link.href = url;
              link.download = 'avatar-profile.png';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }
          }, 'image/png');
        }
        URL.revokeObjectURL(imageObjectUrl);
      };
      img.onerror = () => {
        URL.revokeObjectURL(imageObjectUrl);
        toast.error(t('avatarDecorate.downloadFailed'));
      };
      img.src = imageObjectUrl;
    } catch {
      // 재시도까지 실패한 transient 네트워크/baking 지연 — Sentry 노이즈 방지로
      // 리포트하지 않고 사용자에겐 토스트만(코어와 동일).
      toast.error(t('avatarDecorate.downloadFailed'));
    }
  };
  return <Button variant="contained" intent="neutral" size="sm" leftSlot={<DownloadIcon size="xs" color={ICON_COLOR.neutral} />} onClick={handleDownload} data-sentry-element="Button" data-sentry-component="DownloadAvatarButton" data-sentry-source-file="DownloadAvatarButton.tsx">
      {t('avatarDecorate.download')}
    </Button>;
};